import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with Vite
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconRetinaUrl: iconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  markers?: Array<{
    lat: number;
    lng: number;
    label?: string;
  }>;
  onLocationChange?: (lat: number, lng: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
  interactive?: boolean;
  className?: string;
  showClickMarker?: boolean;
}

// Component to handle map clicks
const MapClickHandler: React.FC<{
  onMapClick?: (lat: number, lng: number) => void;
  onLocationChange?: (lat: number, lng: number) => void;
}> = ({ onMapClick, onLocationChange }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      if (onMapClick) {
        onMapClick(lat, lng);
      }
      if (onLocationChange) {
        onLocationChange(lat, lng);
      }
    },
  });
  return null;
};

// Component to update map center when props change (without changing zoom)
const MapCenter: React.FC<{ center: [number, number]; zoom?: number; updateZoom?: boolean }> = ({ 
  center, 
  zoom,
  updateZoom = false 
}) => {
  const map = useMap();
  useEffect(() => {
    if (updateZoom && zoom !== undefined) {
      map.setView(center, zoom);
    } else {
      // Only update center, preserve current zoom
      map.setView(center, map.getZoom());
    }
  }, [map, center, zoom, updateZoom]);
  return null;
};

const Map: React.FC<MapProps> = ({
  latitude,
  longitude,
  zoom = 13,
  markers = [],
  onLocationChange,
  onMapClick,
  interactive = true,
  className = '',
  showClickMarker = true,
}) => {
  const [clickedLocation, setClickedLocation] = useState<[number, number] | null>(null);
  const [initialZoom] = useState(zoom);
  const [shouldUpdateZoom, setShouldUpdateZoom] = useState(true);

  // Reset clicked location when center changes from outside (e.g., parish selection)
  useEffect(() => {
    if (clickedLocation) {
      const distance = Math.sqrt(
        Math.pow(clickedLocation[0] - latitude, 2) + Math.pow(clickedLocation[1] - longitude, 2)
      );
      // If center moved significantly (more than ~100m), reset clicked location
      if (distance > 0.001) {
        setClickedLocation(null);
        // Allow zoom update when center changes from outside
        setShouldUpdateZoom(true);
      }
    } else {
      // When no clicked location, allow zoom updates for initial load or external changes
      setShouldUpdateZoom(true);
    }
  }, [latitude, longitude, clickedLocation]);

  const handleMapClick = (lat: number, lng: number) => {
    setClickedLocation([lat, lng]);
    // Prevent zoom updates after user clicks on map
    setShouldUpdateZoom(false);
    if (onMapClick) {
      onMapClick(lat, lng);
    }
  };

  const position: [number, number] = [latitude, longitude];
  
  // Extract height from className or use default
  const getHeight = () => {
    if (className.includes('h-')) {
      return undefined; // Let Tailwind handle it
    }
    return '100%'; // Default height
  };

  return (
    <div 
      className={`w-full rounded-lg overflow-hidden relative ${className}`} 
      style={{ height: getHeight() }}
    >
      {interactive && onMapClick && (
        <div className="absolute top-2 left-2 bg-white px-2 py-1 rounded text-xs text-gray-600 z-[1000] shadow-md">
          Click on map to select location
        </div>
      )}
      <MapContainer
        center={position}
        zoom={zoom}
        scrollWheelZoom={interactive}
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        className="rounded-lg"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapCenter center={position} zoom={initialZoom} updateZoom={shouldUpdateZoom} />
        
        {interactive && (
          <MapClickHandler onMapClick={handleMapClick} onLocationChange={onLocationChange} />
        )}

        {/* Show clicked location marker */}
        {clickedLocation && showClickMarker && (
          <Marker
            position={clickedLocation}
            icon={L.divIcon({
              className: 'custom-marker',
              html: `<div style="
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background-color: #3b82f6;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              "></div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            })}
          />
        )}

        {/* Show other markers */}
        {markers.map((marker, index) => (
          <Marker key={index} position={[marker.lat, marker.lng]}>
            {marker.label && (
              <Popup>{marker.label}</Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default Map;

